import {
  type OAuth2Tokens,
  type OAuth2TokenStorage,
  type PKCEState
} from '@docyrus/api-client';

/**
 * ElectronAPI interface matching the preload bridge.
 * The Electron template exposes these IPC methods via contextBridge.
 */
export interface ElectronStoreAPI {
  storeGet: (key: string) => Promise<string | null>;
  storeSet: (key: string, value: string) => Promise<void>;
  storeDelete: (key: string) => Promise<void>;
}

/**
 * OAuth2TokenStorage implementation for Electron apps.
 * Stores tokens via IPC bridge to the main process (electron-store).
 * Falls back to localStorage if electronAPI is not available.
 */
export class ElectronTokenStorage implements OAuth2TokenStorage {
  private readonly tokensKey: string;
  private readonly pkceKey: string;
  private readonly electronAPI: ElectronStoreAPI | null;
  constructor(
    electronAPI?: ElectronStoreAPI,
    keyPrefix: string = 'docyrus_oauth2'
  ) {
    this.electronAPI = electronAPI
      ?? (typeof window !== 'undefined' ? (window as unknown as Record<string, unknown>).electronAPI as ElectronStoreAPI | undefined : undefined)
      ?? null;
    this.tokensKey = `${keyPrefix}_tokens`;
    this.pkceKey = `${keyPrefix}_pkce`;
  }
  async getTokens(): Promise<OAuth2Tokens | null> {
    const data = await this.storeGet(this.tokensKey);

    return data ? JSON.parse(data) : null;
  }
  async setTokens(tokens: OAuth2Tokens): Promise<void> {
    await this.storeSet(this.tokensKey, JSON.stringify(tokens));
  }
  async clearTokens(): Promise<void> {
    await this.storeDelete(this.tokensKey);
  }
  async getPKCEState(): Promise<PKCEState | null> {
    const data = await this.storeGet(this.pkceKey);

    return data ? JSON.parse(data) : null;
  }
  async setPKCEState(state: PKCEState): Promise<void> {
    await this.storeSet(this.pkceKey, JSON.stringify(state));
  }
  async clearPKCEState(): Promise<void> {
    await this.storeDelete(this.pkceKey);
  }
  private async storeGet(key: string): Promise<string | null> {
    if (this.electronAPI) {
      return this.electronAPI.storeGet(key);
    }

    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
  }
  private async storeSet(key: string, value: string): Promise<void> {
    if (this.electronAPI) {
      await this.electronAPI.storeSet(key, value);

      return;
    }

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, value);
    }
  }
  private async storeDelete(key: string): Promise<void> {
    if (this.electronAPI) {
      await this.electronAPI.storeDelete(key);

      return;
    }

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(key);
    }
  }
}

export type { OAuth2TokenStorage };
